import { Loader, Stack, toast } from '@landler/component-library';
import { useQueryClient } from '@tanstack/react-query';
import { ReactNode, useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CoPilotConfig } from '@/api/rest/resources/types/organization';
import { Form as FormProvider } from '@/components';
import { usePathSet } from '@/hooks/usePathSet';
import { Logger } from '@/lib/logs/logger';
import { useCreateCoPilotConfig, useGetCoPilotConfig } from '@/pages/shared/hooks/useCoPilotConfig';
import { buildPath } from '@/utils/buildPath';

const ONBOARDING_FORM_DEFAULT_VALUES: CoPilotConfig = {
  profile: null,
  industry: null,
  goals: [],
  goals_compliance: [],
  goals_context: '',
} as const;

export const FORM_FIELD_NAMES = {
  PROFILE: 'profile',
  INDUSTRY: 'industry',
  GOALS: 'goals',
  GOALS_COMPLIANCE: 'goals_compliance',
  GOALS_CONTEXT: 'goals_context',
} as const;

export const useControlsOnboardingForm = useForm<CoPilotConfig>;
export const useOnboardingFormContext = useFormContext<CoPilotConfig>;

export const OnboardingForm = ({ children }: { children: ReactNode }) => {
  const [isPending, setIsPending] = useState<boolean>(false);
  const { submit } = useCreateCoPilotConfig();
  const { t } = useTranslation();

  const pathSet = usePathSet();
  const navigate = useNavigate();

  const organizationCoPilotConfig = useGetCoPilotConfig();
  const queryClient = useQueryClient();

  const formConfig = useForm<CoPilotConfig>({
    defaultValues: organizationCoPilotConfig.data ?? ONBOARDING_FORM_DEFAULT_VALUES,
  });

  const handleSubmit = async (data: CoPilotConfig) => {
    try {
      setIsPending(true);

      await submit(data);

      toast({
        title: t('shared.onboarding.toast.success.description'),
        type: 'success',
      });

      await queryClient.invalidateQueries({
        queryKey: ['organization'],
      });
    } catch (error: unknown) {
      if (error instanceof Error) {
        Logger.error(`Error submitting onboarding form ${error.message}`);

        toast({
          type: 'error',
          title: t('shared.onboarding.toast.error.description'),
          description: error.message,
        });
      }
    }

    setIsPending(false);

    navigate(buildPath(pathSet.home));
  };

  return (
    <FormProvider {...formConfig}>
      {isPending ? (
        <Stack className='h-full' center centerMain spacing={4}>
          <div>
            <Loader className='min-h-fit' />
          </div>
          <span className='typography-display4'>{t('shared.onboarding.processingData')}</span>
        </Stack>
      ) : (
        <form onSubmit={formConfig.handleSubmit(handleSubmit)}>{children}</form>
      )}
    </FormProvider>
  );
};
